import { useState } from 'react';
import batchDeleteVessels from '../../../api/vessel-batch-delete';
import { useAppSelector } from '../../../hooks';
import useUserRoleAndShortId from '../../../hooks/user-details/useUserDetails';
import {
  DeleteMyFleetVesselsResponse,
  MAX_VESSEL_DELETION_BATCH_SIZE,
  MAX_VESSEL_DELETION_CONCURRENCY_SIZE,
  MyFleetActionButtonGroupStatus,
  MyFleetDialogStatus,
  MyFleetVessels,
} from '../../../models/my-fleet.model';
import {
  getCountAnalyticsEventLabel,
  getUserAnalyticsEventLabel,
  logAnalyticsEvent,
} from '../../../utils/analytics';
import MyFleetVesselDeletionButtonGroup from './my-fleet-delete-vessels-button-group/my-fleet-delete-vessels-button-group';
import MyFleetVesselsDeleteDialog from './my-fleet-delete-vessels-dialog/my-fleet-delete-vessels-dialog';

interface MyFleetVesselDeletionButtonGroupProps {
  myFleetVessels: MyFleetVessels | null;
  selectedImos: string[];
  setSelectedImos: (selectedImos: string[]) => void;
  deleteActive: boolean;
  resetState: () => void;
  setDeleteActive: (status: MyFleetActionButtonGroupStatus) => void;
  onDeleteSuccess: () => void;
}

function MyFleetVesselDeletion({
  myFleetVessels,
  selectedImos,
  setSelectedImos,
  deleteActive,
  resetState,
  setDeleteActive,
  onDeleteSuccess,
}: MyFleetVesselDeletionButtonGroupProps) {
  const totalVessels = myFleetVessels?.length || 0;
  const [openDialog, setOpenDialog] = useState(false);
  const userDetails = useUserRoleAndShortId();
  const { proAlerts } = useAppSelector((state) => state.proAlerts);
  const [status, setStatus] = useState<MyFleetDialogStatus>(
    MyFleetDialogStatus.Idle
  );
  const [deleteVesselResponse, setDeleteVesselResponse] =
    useState<DeleteMyFleetVesselsResponse | null>(null);
  const [batchProgress, setBatchProgress] = useState<{
    completed: number;
    total: number;
    failed: number;
  }>({ completed: 0, total: 0, failed: 0 });
  const [batchErrors, setBatchErrors] = useState<any[]>([]);

  const handleDelete = () => {
    setStatus(MyFleetDialogStatus.Idle);
    setDeleteVesselResponse(null);
    setOpenDialog(true);
  };

  const handleDeleteConfirmed = async () => {
    setStatus(MyFleetDialogStatus.Loading);
    setDeleteVesselResponse(null);
    setBatchProgress({ completed: 0, total: selectedImos.length, failed: 0 });
    setBatchErrors([]);
    setOpenDialog(true);

    try {
      const { deleted, errors } = await batchDeleteVessels({
        vesselImos: selectedImos,
        batchSize: MAX_VESSEL_DELETION_BATCH_SIZE,
        concurrency: MAX_VESSEL_DELETION_CONCURRENCY_SIZE,
        setProgress: ({ completed, total, failed }) => {
          setBatchProgress({ completed, total, failed });
        },
        setErrors: (errs) => {
          setBatchErrors(errs);
        },
        setResponse: (resp) => {
          setDeleteVesselResponse(resp);
        },
      });

      // Analytics and status
      const vesselsWithAlertsCount = selectedImos.filter((imo) => {
        const vessel = myFleetVessels?.find((v) => v.imo === imo);
        if (!vessel) return false;
        return proAlerts?.some((alert) => alert.vessel_id === vessel.vessel_id);
      }).length;
      logAnalyticsEvent('MyFleet', 'VesselsDeleted', [
        getUserAnalyticsEventLabel(userDetails),
        getCountAnalyticsEventLabel(deleted.length),
      ]);
      logAnalyticsEvent('MyFleet', 'ProAlertsDeleted', [
        getUserAnalyticsEventLabel(userDetails),
        getCountAnalyticsEventLabel(vesselsWithAlertsCount),
      ]);
      setStatus(
        errors.length > 0
          ? MyFleetDialogStatus.Error
          : MyFleetDialogStatus.Success
      );
    } catch (error: any) {
      setStatus(MyFleetDialogStatus.Error);
      setDeleteVesselResponse(error.response?.data ?? null);
    }
  };

  const handleDialogClose = () => {
    setOpenDialog(false);
    if (onDeleteSuccess && status === MyFleetDialogStatus.Success)
      onDeleteSuccess();
  };

  const handleSelectAll = () => {
    if (selectedImos.length === totalVessels) {
      setSelectedImos([]);
    } else {
      const myFleetVesselsImos =
        myFleetVessels?.map((vessel) => vessel.imo) || [];
      setSelectedImos(myFleetVesselsImos);
    }
  };
  const handleShowActions = () => {
    setDeleteActive(MyFleetActionButtonGroupStatus.Delete);
    if (deleteActive) {
      setSelectedImos([]);
    }
  };

  const handleCancel = () => {
    setDeleteActive(MyFleetActionButtonGroupStatus.Idle);
    resetState();
  };
  const allVesselsSelected = () => selectedImos.length === totalVessels;

  const deleteButtonLabel = () =>
    `Delete (${selectedImos.length}) ${
      selectedImos.length > 1 ? 'Vessels' : 'Vessel'
    }`;

  const getSelectedVessels = () =>
    myFleetVessels?.filter((vessel) => selectedImos.includes(vessel.imo)) || [];

  return (
    <>
      <MyFleetVesselDeletionButtonGroup
        showActionButtons={deleteActive}
        onShowActions={handleShowActions}
        onDelete={handleDelete}
        onCancel={handleCancel}
        onSelectAll={handleSelectAll}
        allSelected={allVesselsSelected()}
        deleteDisabled={selectedImos.length === 0}
        deleteButtonLabel={deleteButtonLabel()}
      />
      <MyFleetVesselsDeleteDialog
        open={openDialog}
        onCancel={handleDialogClose}
        onClose={handleDialogClose}
        onConfirm={handleDeleteConfirmed}
        status={status}
        response={deleteVesselResponse}
        vessels={getSelectedVessels() || []}
        batchProgress={batchProgress}
        batchErrors={batchErrors}
      />
    </>
  );
}

export default MyFleetVesselDeletion;
